import apiClient from '../axios.js';

const BASE = '/client/products';
const ATTRIBUTES_BASE = '/client/product-attributes';
const CATEGORIES_BASE = '/client/product-categories';

export const productService = {
  /**
   * 查詢商品列表（分頁）
   * @param {Object} params - page, pageSize, search, status
   */
  getProducts: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page != null) queryParams.append('page', params.page);
    if (params.pageSize != null) queryParams.append('pageSize', params.pageSize);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.includeB2bPriceSet) queryParams.append('includeB2bPriceSet', 'true');
    const qs = queryParams.toString();
    const response = await apiClient.get(`${BASE}${qs ? `?${qs}` : ''}`);
    return response;
  },

  /**
   * 依條碼查詢商品或規格
   * @param {string} barcode
   */
  getByBarcode: async (barcode) => {
    const response = await apiClient.get(`${BASE}/by-barcode`, { params: { barcode } });
    return response;
  },

  /**
   * 查詢商品詳情（含規格、圖片）
   * @param {string} id - 商品 ID
   */
  getProduct: async (id) => {
    const response = await apiClient.get(`${BASE}/${id}`);
    return response;
  },

  /**
   * 新增商品
   * @param {Object} data - name, summary, introduction, specDescription, returnDescription, status, weightKg, ...
   */
  createProduct: async (data) => {
    const response = await apiClient.post(BASE, data);
    return response;
  },

  /**
   * 更新商品
   */
  updateProduct: async (id, data) => {
    const response = await apiClient.put(`${BASE}/${id}`, data);
    return response;
  },

  /**
   * 刪除商品
   */
  deleteProduct: async (id) => {
    const response = await apiClient.delete(`${BASE}/${id}`);
    return response;
  },

  /**
   * 查詢商品規格列表
   */
  listVariants: async (productId) => {
    const response = await apiClient.get(`${BASE}/${productId}/variants`);
    return response;
  },

  /**
   * 新增規格
   */
  createVariant: async (productId, data) => {
    const response = await apiClient.post(`${BASE}/${productId}/variants`, data);
    return response;
  },

  /**
   * 更新規格
   */
  updateVariant: async (productId, variantId, data) => {
    const response = await apiClient.put(`${BASE}/${productId}/variants/${variantId}`, data);
    return response;
  },

  deleteVariant: async (productId, variantId) => {
    const response = await apiClient.delete(`${BASE}/${productId}/variants/${variantId}`);
    return response;
  },

  /**
   * 查詢商品圖片列表
   * @param {string} productId
   * @param {boolean} variantIdNullOnly - 是否只取共用圖（無 variantId）
   */
  listProductImages: async (productId, variantIdNullOnly = true) => {
    const params = variantIdNullOnly ? { variantIdNullOnly: true } : {};
    const response = await apiClient.get(`${BASE}/${productId}/images`, { params });
    return response;
  },

  /**
   * 新增商品圖片
   */
  createProductImage: async (productId, data) => {
    const response = await apiClient.post(`${BASE}/${productId}/images`, data);
    return response;
  },

  /**
   * 刪除商品圖片
   */
  deleteProductImage: async (productId, imageId) => {
    const response = await apiClient.delete(`${BASE}/${productId}/images/${imageId}`);
    return response;
  },

  /**
   * 查詢規格完整顯示圖片（共用圖 + 規格專屬圖）
   */
  getVariantImages: async (variantId) => {
    const response = await apiClient.get(`${BASE}/variants/${variantId}/images`);
    return response;
  },

  /**
   * 查詢商品屬性定義列表（含選項）
   */
  getAttributeDefinitions: async () => {
    const response = await apiClient.get(ATTRIBUTES_BASE);
    return response;
  },

  /**
   * 新增商品屬性定義
   */
  createAttributeDefinition: async (data) => {
    const response = await apiClient.post(ATTRIBUTES_BASE, data);
    return response;
  },

  /**
   * 取得商品圖片上傳 Presigned URL
   * @param {string} productId
   * @param {Object} uploadData - { fileName, fileType, fileSize }
   */
  getUploadUrl: async (productId, uploadData) => {
    const response = await apiClient.post(`${BASE}/${productId}/get-upload-url`, uploadData);
    return response;
  },

  /**
   * 確認圖片上傳完成（寫入 product_images）
   * @param {string} productId
   * @param {Object} data - { objectKey, variantId?, isPrimary?, altText? }
   */
  confirmUpload: async (productId, data) => {
    const response = await apiClient.post(`${BASE}/${productId}/confirm-upload`, data);
    return response;
  },

  /**
   * 更新商品屬性定義
   */
  updateAttributeDefinition: async (id, data) => {
    const response = await apiClient.put(`${ATTRIBUTES_BASE}/${id}`, data);
    return response;
  },

  /**
   * 刪除商品屬性定義
   */
  deleteAttributeDefinition: async (id) => {
    const response = await apiClient.delete(`${ATTRIBUTES_BASE}/${id}`);
    return response;
  },

  // ===== 套組組件 =====

  /**
   * 取得套組商品的組件列表
   * @param {string} productId - 套組商品 ID
   */
  getBundleItems: async (productId) => {
    const response = await apiClient.get(`${BASE}/${productId}/bundle-items`);
    return response;
  },

  /**
   * 更新套組商品的組件清單（全量覆寫）
   * @param {string} productId - 套組商品 ID
   * @param {Array} items - 組件清單 [{componentProductId, componentVariantId, quantity, sortOrder}]
   */
  updateBundleItems: async (productId, items) => {
    const response = await apiClient.put(`${BASE}/${productId}/bundle-items`, { items });
    return response;
  },

  // ===== 無規格商品庫存（預設規格）=====
  /**
   * 取得無規格商品的庫存數量（預設規格的 stockQuantity）
   * @param {string} productId
   */
  getDefaultStock: async (productId) => {
    const response = await apiClient.get(`${BASE}/${productId}/default-stock`);
    return response;
  },

  /**
   * 設定無規格商品的庫存數量（upsert 預設規格）
   * @param {string} productId
   * @param {number|null} stockQuantity
   */
  setDefaultStock: async (productId, stockQuantity) => {
    const response = await apiClient.put(`${BASE}/${productId}/default-stock`, { stockQuantity });
    return response;
  },

  // ===== 商品分類 =====
  getCategoriesTree: async () => {
    const response = await apiClient.get(CATEGORIES_BASE);
    return response;
  },
  getCategoriesFlat: async () => {
    const response = await apiClient.get(`${CATEGORIES_BASE}/flat`);
    return response;
  },
  createCategory: async (data) => {
    const response = await apiClient.post(CATEGORIES_BASE, data);
    return response;
  },
  updateCategory: async (id, data) => {
    const response = await apiClient.put(`${CATEGORIES_BASE}/${id}`, data);
    return response;
  },
  deleteCategory: async (id) => {
    const response = await apiClient.delete(`${CATEGORIES_BASE}/${id}`);
    return response;
  },
};
